import React, { useRef } from 'react';
import { Printer, X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

export default function PrintableInvoice({ invoice, open, onClose, companyName, companyEmail, companyPhone }) {
  const printRef = useRef();
  const displayName = companyName || 'Your Dealership';
  const displayEmail = companyEmail || '';
  const displayPhone = companyPhone || '';

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open('', '_blank', 'width=800,height=900');
    win.document.write(`<!DOCTYPE html><html><head><title>Invoice ${invoice.invoice_number}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; padding: 40px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 3px solid #111; }
        .logo-area h1 { font-size: 28px; font-weight: 800; letter-spacing: -0.5px; }
        .logo-area p { color: #666; font-size: 13px; margin-top: 4px; }
        .invoice-meta { text-align: right; }
        .invoice-meta .inv-num { font-size: 22px; font-weight: 700; color: #111; }
        .invoice-meta p { font-size: 13px; color: #666; margin-top: 4px; }
        .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .status-paid { background: #dcfce7; color: #166534; }
        .status-pending { background: #fef3c7; color: #92400e; }
        .status-partial { background: #dbeafe; color: #1e40af; }
        .status-refunded { background: #fee2e2; color: #991b1b; }
        .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px; }
        .detail-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; padding: 18px; }
        .detail-box h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #9ca3af; font-weight: 600; margin-bottom: 10px; }
        .detail-box p { font-size: 14px; margin-bottom: 4px; }
        .detail-box strong { color: #111; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
        thead th { background: #111; color: #fff; text-align: left; padding: 12px 16px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
        thead th:last-child { text-align: right; }
        tbody td { padding: 14px 16px; border-bottom: 1px solid #e5e7eb; font-size: 14px; }
        tbody td:last-child { text-align: right; font-weight: 600; }
        .totals { display: flex; justify-content: flex-end; }
        .totals-box { width: 280px; }
        .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; color: #666; }
        .totals-row.total { border-top: 2px solid #111; padding-top: 12px; margin-top: 4px; font-size: 18px; font-weight: 700; color: #111; }
        .footer { margin-top: 48px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
        .remarks { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 14px 18px; margin-bottom: 24px; font-size: 13px; color: #92400e; }
        @media print { body { padding: 20px; } }
      </style>
    </head><body>${content.innerHTML}
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}<\/script>
    </body></html>`);
    win.document.close();
  };

  if (!invoice) return null;

  const statusClass = `status-${invoice.status || 'pending'}`;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-0">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold">Invoice Preview</h2>
          <div className="flex items-center gap-2">
            <Button onClick={handlePrint} className="bg-gray-900 text-white rounded-xl" data-testid="print-invoice-btn">
              <Printer className="w-4 h-4 mr-2" /> Print Invoice
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div ref={printRef} className="p-8" data-testid="printable-invoice-content">
          <div className="header">
            <div className="logo-area">
              <h1>{displayName.toUpperCase()}</h1>
              <p>Auto Sales & Technology Solutions</p>
              {displayPhone && <p>{displayPhone}</p>}
              {displayEmail && <p>{displayEmail}</p>}
            </div>
            <div className="invoice-meta">
              <div className="inv-num">{invoice.invoice_number}</div>
              <p>Date: {invoice.sale_date || invoice.created_at?.split('T')[0] || '—'}</p>
              {invoice.deal_date && <p>Deal Date: {invoice.deal_date}</p>}
              <p style={{marginTop: '8px'}}><span className={`status-badge ${statusClass}`}>{invoice.status}</span></p>
            </div>
          </div>

          <div className="details-grid">
            <div className="detail-box">
              <h3>Bill To</h3>
              <p><strong>{invoice.customer_name || 'Walk-in Customer'}</strong></p>
              {invoice.customer_id && <p style={{color: '#666', fontSize: '12px'}}>ID: {invoice.customer_id}</p>}
            </div>
            <div className="detail-box">
              <h3>Vehicle / Stock</h3>
              <p><strong>Stock #:</strong> {invoice.stock_number || '—'}</p>
              {invoice.salesperson && <p><strong>Salesperson:</strong> {invoice.salesperson}</p>}
              <p><strong>Payment:</strong> {(invoice.payment_type || 'cash').charAt(0).toUpperCase() + (invoice.payment_type || 'cash').slice(1)}</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style={{textAlign: 'right'}}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Vehicle Sale Price</td>
                <td>${(invoice.sale_price || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
              </tr>
              {(invoice.dealer_fee || 0) > 0 && (
                <tr>
                  <td>Dealer Processing Fee</td>
                  <td>${(invoice.dealer_fee).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                </tr>
              )}
              {(invoice.sales_tax || 0) > 0 && (
                <tr>
                  <td>Sales Tax</td>
                  <td>${(invoice.sales_tax).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="totals">
            <div className="totals-box">
              <div className="totals-row">
                <span>Subtotal</span>
                <span>${(invoice.sale_price || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
              {(invoice.dealer_fee || 0) > 0 && (
                <div className="totals-row">
                  <span>Dealer Fee</span>
                  <span>${(invoice.dealer_fee).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              )}
              {(invoice.sales_tax || 0) > 0 && (
                <div className="totals-row">
                  <span>Sales Tax</span>
                  <span>${(invoice.sales_tax).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              )}
              <div className="totals-row total">
                <span>Total Due</span>
                <span>${(invoice.total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
            </div>
          </div>

          {invoice.remarks && (
            <div className="remarks">
              <strong>Remarks:</strong> {invoice.remarks}
            </div>
          )}

          <div className="footer">
            <p>Thank you for your business!</p>
            <p style={{marginTop: '4px'}}>{displayName}{displayPhone ? ` | ${displayPhone}` : ''}{displayEmail ? ` | ${displayEmail}` : ''}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
